(function () {
  'use strict';

  const debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  class SearchModal {
    constructor() {
      this.modal = document.querySelector('[data-search-modal]');
      if (!this.modal) return;

      this.overlay = this.modal.querySelector('[data-search-modal-overlay]');
      this.form = this.modal.querySelector('[data-search-form]');
      this.input = this.modal.querySelector('[data-search-input]');
      this.clearBtn = this.modal.querySelector('[data-search-clear]');
      this.body = this.modal.querySelector('[data-search-body]');
      this.status = this.modal.querySelector('[data-search-status]');
      this.loading = this.modal.querySelector('[data-search-loading]');
      this.empty = this.modal.querySelector('[data-search-empty]');
      this.resultsWrap = this.modal.querySelector('[data-search-results-wrap]');
      this.productsSection = this.modal.querySelector('[data-search-products-section]');
      this.productsGrid = this.modal.querySelector('[data-search-products]');
      this.productsHeading = this.modal.querySelector('[data-search-products-heading]');
      this.otherSection = this.modal.querySelector('[data-search-other-section]');
      this.otherList = this.modal.querySelector('[data-search-other]');
      this.popular = this.modal.querySelector('[data-search-popular]');
      this.footer = this.modal.querySelector('[data-search-footer]');
      this.viewAll = this.modal.querySelector('[data-search-view-all]');

      this.searchUrl = window.themeSettings?.searchUrl || '/search';
      this.activeIndex = -1;
      this.requestId = 0;

      this.init();
    }

    init() {
      document.querySelectorAll('[data-search-toggle]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      this.modal.querySelector('[data-search-modal-close]')?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click', () => this.close());
      this.clearBtn?.addEventListener('click', () => this.clearInput());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });

      this.input?.addEventListener('input', debounce(() => this.onInput(), 260));
      this.input?.addEventListener('keydown', (e) => this.onKeydown(e));

      this.form?.addEventListener('submit', (e) => {
        const query = this.input.value.trim();
        if (!query) {
          e.preventDefault();
          return;
        }
        this.close();
      });

      window.LuxeraSearchModal = this;
    }

    isOpen() {
      return this.modal.classList.contains('is-open');
    }

    open() {
      document.querySelector('[data-mobile-menu]')?.classList.remove('is-open');
      document.querySelector('[data-mobile-menu-overlay]')?.classList.remove('is-open');

      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('search-modal-open');

      requestAnimationFrame(() => {
        this.input?.focus();
        this.input?.select();
      });
    }

    close() {
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('search-modal-open');
      this.clearInput(false);
    }

    clearInput(focus = true) {
      if (this.input) this.input.value = '';
      this.toggleClearButton();
      this.resetResults();
      if (focus) this.input?.focus();
    }

    toggleClearButton() {
      if (!this.clearBtn || !this.input) return;
      const hasValue = this.input.value.trim().length > 0;
      this.clearBtn.hidden = !hasValue;
    }

    resetResults() {
      this.requestId += 1;
      this.loading.hidden = true;
      this.empty.hidden = true;
      this.resultsWrap.hidden = true;
      this.footer.hidden = true;
      this.popular.hidden = false;
      this.productsSection.hidden = true;
      this.otherSection.hidden = true;
      this.productsGrid.innerHTML = '';
      this.otherList.innerHTML = '';
      this.status.textContent = '';
      this.activeIndex = -1;
      this.toggleClearButton();
    }

    onInput() {
      const query = this.input.value.trim();
      this.toggleClearButton();

      if (query.length < 2) {
        this.resetResults();
        return;
      }

      this.fetchResults(query);
    }

    async fetchResults(query) {
      const currentRequest = ++this.requestId;

      this.popular.hidden = true;
      this.empty.hidden = true;
      this.resultsWrap.hidden = true;
      this.footer.hidden = true;
      this.loading.hidden = false;
      this.status.textContent = '';

      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,collection,page,article',
        'resources[limit]': '12',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last'
      });

      try {
        const res = await fetch(`/search/suggest.json?${params.toString()}`);
        if (!res.ok) throw new Error('Search failed');
        const data = await res.json();

        if (currentRequest !== this.requestId) return;

        this.renderResults(query, data.resources?.results || {});
      } catch (err) {
        if (currentRequest !== this.requestId) return;
        console.error(err);
        this.showEmpty();
      } finally {
        if (currentRequest === this.requestId) {
          this.loading.hidden = true;
        }
      }
    }

    renderResults(query, results) {
      const products = results.products || [];
      const collections = results.collections || [];
      const pages = results.pages || [];
      const articles = results.articles || [];
      const otherItems = [
        ...collections.map((item) => ({ type: 'collection', ...item })),
        ...pages.map((item) => ({ type: 'page', ...item })),
        ...articles.map((item) => ({ type: 'article', ...item }))
      ];

      if (!products.length && !otherItems.length) {
        this.showEmpty();
        return;
      }

      this.empty.hidden = true;
      this.resultsWrap.hidden = false;
      this.footer.hidden = false;
      this.viewAll.href = `${this.searchUrl}?q=${encodeURIComponent(query)}&type=product`;

      if (products.length) {
        this.productsSection.hidden = false;
        this.productsHeading.textContent = `Products (${products.length})`;
        this.productsGrid.innerHTML = products.map((product, index) => this.renderProductCard(product, index)).join('');
      } else {
        this.productsSection.hidden = true;
        this.productsGrid.innerHTML = '';
      }

      if (otherItems.length) {
        this.otherSection.hidden = false;
        this.otherList.innerHTML = otherItems
          .map((item, index) => this.renderOtherItem(item, products.length + index))
          .join('');
      } else {
        this.otherSection.hidden = true;
        this.otherList.innerHTML = '';
      }

      const total = products.length + otherItems.length;
      this.status.textContent = `${total} suggestion${total === 1 ? '' : 's'} for "${query}"`;
      this.activeIndex = -1;
    }

    renderProductCard(product, index) {
      const imageUrl = this.getProductImage(product);
      const price = this.getProductPrice(product);
      const imageMarkup = imageUrl
        ? `<img src="${this.escapeAttr(imageUrl)}" alt="" class="search-modal__product-image" width="400" height="400" loading="lazy">`
        : `<span class="search-modal__product-placeholder" aria-hidden="true">${this.escapeHtml(product.title.charAt(0))}</span>`;

      return `
        <a href="${this.escapeAttr(product.url)}" class="search-modal__product-card" data-search-result data-index="${index}">
          <span class="search-modal__product-media">${imageMarkup}</span>
          <span class="search-modal__product-info">
            <span class="search-modal__product-title">${this.escapeHtml(product.title)}</span>
            <span class="search-modal__product-price">
              <span class="search-modal__price-current">${this.escapeHtml(price)}</span>
            </span>
          </span>
        </a>
      `;
    }

    renderOtherItem(item, index) {
      const imageUrl = item.image || item.featured_image?.url || '';
      const imageMarkup = imageUrl
        ? `<img src="${this.escapeAttr(imageUrl)}" alt="" class="search-modal__list-image" width="48" height="48" loading="lazy">`
        : `<span class="search-modal__list-placeholder" aria-hidden="true">${this.typeLabel(item.type).charAt(0)}</span>`;

      return `
        <a href="${this.escapeAttr(item.url)}" class="search-modal__list-item" data-search-result data-index="${index}">
          ${imageMarkup}
          <span class="search-modal__list-text">
            <span class="search-modal__list-title">${this.escapeHtml(item.title)}</span>
            <span class="search-modal__list-meta">${this.typeLabel(item.type)}</span>
          </span>
        </a>
      `;
    }

    getProductImage(product) {
      if (typeof product.image === 'string' && product.image) return product.image;
      if (product.featured_image?.url) return product.featured_image.url;
      return '';
    }

    getProductPrice(product) {
      if (product.price) return product.price;
      if (product.price_min && product.price_max && product.price_min !== product.price_max) {
        return `${product.price_min} – ${product.price_max}`;
      }
      return product.price_min || product.price_max || '';
    }

    showEmpty() {
      this.resultsWrap.hidden = true;
      this.footer.hidden = true;
      this.productsSection.hidden = true;
      this.otherSection.hidden = true;
      this.popular.hidden = true;
      this.empty.hidden = false;
      this.status.textContent = '';
    }

    typeLabel(type) {
      const labels = {
        collection: 'Collection',
        page: 'Page',
        article: 'Article'
      };
      return labels[type] || 'Result';
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text ?? '';
      return div.innerHTML;
    }

    escapeAttr(text) {
      return String(text ?? '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }

    onKeydown(e) {
      if (!this.isOpen()) return;

      const links = [...this.modal.querySelectorAll('[data-search-result]')];
      if (!links.length) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.activeIndex = Math.min(this.activeIndex + 1, links.length - 1);
        this.highlightResult(links);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.activeIndex = Math.max(this.activeIndex - 1, 0);
        this.highlightResult(links);
      } else if (e.key === 'Enter' && this.activeIndex >= 0) {
        e.preventDefault();
        links[this.activeIndex].click();
      }
    }

    highlightResult(links) {
      links.forEach((link, i) => {
        link.classList.toggle('is-active', i === this.activeIndex);
      });
      links[this.activeIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  document.addEventListener('DOMContentLoaded', () => new SearchModal());
})();
